import { useAtomValue } from 'jotai';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { getAuth, signOut } from 'firebase/auth';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';

import { pushLearnerError } from 'src/utils/analytics';

import store from 'src/store';

import useJoin from './use-join';
import { useRoute } from './use-route';
import { useModal } from './use-modal';
import { useResponsive } from './use-responsive';

export default function useLoginError() {
  const snackbar = useSnackbar();
  const { t } = useTranslation();
  const route = useRoute();
  const join = useJoin();
  const login = useAtomValue(store.login.state);
  const { openModal } = useModal();
  const managerInfo = useAtomValue(store.manager.mangerInfoAtom);
  const BRAND_MANAGER_EMAIL = managerInfo?.email;
  const BRAND_MANAGER_PHONE = managerInfo?.phone;
  const isMobile = useResponsive('down', 'md');

  const openAlertDialog = (
    title: string,
    content: string,
    managerEmail: string,
    managerPhone: string,
    accept: any,
    buttonColor: any = 'inherit'
  ) => {
    openModal({
      type: 'alert',
      data: {
        title,
        content: (
          <Stack spacing={2}>
            <Typography variant="body1">{content}</Typography>
            <Stack spacing={0.5} color="var(--text-secondary)">
              <Typography variant="subtitle1">{managerEmail}</Typography>
              <Typography variant="subtitle1">{managerPhone}</Typography>
            </Stack>
          </Stack>
        ),
        accept,
        buttonColor,
      },
    });
  };

  const handleLoginError = (error: any, loginType: any) => {
    const responseData = error?.response?.data?.meta;
    const totalFailCount = 5;
    const isSocialLogin = Boolean(loginType?.social);

    // 로그인 실패 분석용 이벤트입니다.
    // 공통 API 에러 핸들러에서는 로그인 비즈니스 에러를 중복 수집하지 않도록 했기 때문에,
    // 실제 로그인 실패 사유는 이 지점에서 action_name과 함께 한 번만 보냅니다.
    // 이메일/아이디/이름/전화번호 같은 입력값 원문은 절대 보내지 않습니다.
    pushLearnerError({
      error_type: 'login',
      http_status: error?.response?.status,
      error_code: responseData?.errCode,
      api_path: error?.config?.url?.split('?')[0],
      api_method: String(error?.config?.method ?? '').toUpperCase(),
      screen_name: 'login',
      action_name: isSocialLogin ? 'social_login' : 'email_login',
      handled: true,
    });

    switch (responseData?.errCode) {
      case 1201:
      case 1202:
        openAlertDialog(
          t('learner.779'),
          t('learner.780'),
          t('learner.781', { str: BRAND_MANAGER_EMAIL }),
          t('learner.782', { str: BRAND_MANAGER_PHONE }),
          t('learner.783')
        );
        break;

      case 1204:
        openAlertDialog(
          t('learner.1482'),
          t('learner.1483'),
          t('learner.1484', { str: BRAND_MANAGER_EMAIL }),
          t('learner.1485', { str: BRAND_MANAGER_PHONE }),
          t('learner.1486')
        );
        break;

      case 1205:
        openAlertDialog(
          t('learner.1914'),
          `${t('learner.1915')} ${t('learner.1916')}`,
          t('learner.1917', { str: BRAND_MANAGER_EMAIL }),
          t('learner.1918', { str: BRAND_MANAGER_PHONE }),
          t('learner.1919')
        );
        break;

      case 1206:
        openAlertDialog(
          t('learner.3866'),
          t('learner.3867'),
          t('learner.3868', { str: BRAND_MANAGER_EMAIL }),
          t('learner.3869', { str: BRAND_MANAGER_PHONE }),
          t('learner.3875')
        );
        break;

      case 1207:
        openAlertDialog(
          t('learner.3871'),
          t('learner.3872'),
          t('learner.3873', { str: BRAND_MANAGER_EMAIL }),
          t('learner.3874', { str: BRAND_MANAGER_PHONE }),
          t('learner.3875')
        );
        break;

      case 1105:
        openModal({
          type: 'alert',
          data: {
            content: t('learner.1875'),
            accept: t('learner.1876'),
          },
        });
        break;

      case 1335:
        snackbar.enqueueSnackbar(t('learner.323'), {
          anchorOrigin: { horizontal: isMobile ? 'center' : 'left', vertical: 'bottom' },
          variant: 'default',
        });
        break;

      case 1200:
        snackbar.enqueueSnackbar(
          `${t('learner.356')} (${responseData?.data?.failCount}/${totalFailCount})`,
          {
            anchorOrigin: { horizontal: isMobile ? 'center' : 'left', vertical: 'bottom' },
            variant: 'default',
          }
        );
        break;

      default:
        if (isSocialLogin) {
          openModal({
            type: 'confirm',
            data: {
              title: t('learner.300'),
              content: [t('learner.301'), t('learner.302')],
              accept: t('learner.304'),
              onAccept: () => {
                join.setJoinStart(true);
                join.setNameFirst(login.socialFirstName);
                join.setNameLast(login.socialLastName);
                join.setNameFull(login.socialFullName);
                join.setSocialLink({
                  type: 'firebase',
                  name: login.socialType?.toUpperCase(),
                  token: login.socialToken,
                  socialType: loginType,
                  userloginLanguage: login.socialLoginLanguage,
                });

                const pushTokenByReactNative = localStorage.getItem('fbToken');
                join.setPushNotification({
                  pushToken: pushTokenByReactNative ?? '',
                  pushLanguage: join.getUserData().socialLink.userloginLanguage,
                });

                join.setSocialId(login.socialEmail);
                join.setLoginId(login.socialEmail);
                route.pushNavigate(`${paths.join.policy.root}?type=social`);
              },
              cancel: t('learner.303'),
              onCancel: () => {
                signOutFirebase();
              },
            },
          });
        } else {
          snackbar.enqueueSnackbar(t('learner.356'), {
            anchorOrigin: { horizontal: isMobile ? 'center' : 'left', vertical: 'bottom' },
            variant: 'default',
          });
        }
        break;
    }
  };

  const signOutFirebase = async () => {
    const auth = getAuth();
    await signOut(auth);
  };

  return { handleLoginError, signOutFirebase };
}
